import './kp-media-viewer-search.less';
import {ngAsync, ngComponentCssClass} from 'shared/utils/ng-@decorators';
import type {State} from '../../commons/constants/portaro.constants';
import type {IAugmentedJQuery, IOnChanges, IPostLink} from 'angular';
import type SearchApi from '../search-api';

export abstract class KpMediaViewerSearchComponentController implements KpMediaViewerSearchComponentBindings, IPostLink, IOnChanges {
    public query: string;
    public focus: boolean;

    public abstract onSearch(locals: { $query: string }): Promise<number>;

    public abstract onCancel();

    public abstract onPrevious(locals: { $index: number });

    public abstract onNext(locals: { $index: number });

    public abstract searchApi(locals: { $api: SearchApi });

    public resultsCount: number;
    public state: State = 'INITIAL';
    public currentIndex: number;
    private inputElement: HTMLInputElement;

    /*@ngInject*/
    constructor(private $element: IAugmentedJQuery) {
    }

    public $onChanges(onChangesObj: angular.IOnChangesObject): void {
        if (onChangesObj.focus && onChangesObj.focus.currentValue === true) {
            this.getInputElement().focus();
        }
    }

    public $postLink(): void {
        this.searchApi({
            $api: {
                search: this.search.bind(this),
                next: this.next.bind(this),
                previous: this.previous.bind(this),
                cancel: this.cancel.bind(this)
            }
        });
    }

    @ngAsync()
    public async search(query: string): Promise<number> {
        if (query) {
            this.query = query;

            this.state = 'PROCESSING';
            this.currentIndex = 0;

            this.resultsCount = await this.onSearch({$query: query});

            this.state = 'NORMAL';

            return this.resultsCount;
        }
    }

    @ngAsync()
    public async previous(): Promise<number> {
        const correctIndex = await this.onPrevious({$index: this.currentIndex - 1});

        if (typeof correctIndex === 'number') {
            this.currentIndex = correctIndex;
        } else {
            this.currentIndex--;
        }

        return this.currentIndex;
    }

    @ngAsync()
    public async next(): Promise<number> {
        const correctIndex = await this.onNext({$index: this.currentIndex + 1});

        if (typeof correctIndex === 'number') {
            this.currentIndex = correctIndex;
        } else {
            this.currentIndex++;
        }

        return this.currentIndex;
    }

    public cancel() {
        this.state = 'INITIAL';
        this.query = '';

        this.onCancel();
    }

    private getInputElement(): HTMLInputElement {
        return this.$element[0].querySelector('.search-input');
    }
}

/**
 * @ngdoc component
 * @name kpMediaViewerSearch
 * @module portaro.media-viewer
 *
 * @param {string} queryByDirectory Search query
 * @param {boolean} focus If `true`, input is focused.
 * @param {function(): Promise<number>} onSearch Function executed on search triggered, returning total number of items found.
 * @param {string} onSearch.$queryByDirectory Search query
 * @param {function} onCancel Expression evaluated on search cancellation.
 * @param {function(): Promise<number>} onPrevious Function executed if previous button is clicked. If function returns number, this value is index of currently found item. If no return value is given, component has internal counter of current index.
 * @param {function(): number} onPrevious.$index Predicted next index of found item.
 * @param {function(): Promise<number>} onNext Function executed if next button is clicked. If function returns number, this value is index of currently found item. If no return value is given, component has internal counter of current index.
 * @param {function(): number} onNext.$index Predicted next index of found item.
 * @param {function()} [searchApi] Connection to search api.
 * @param {SearchApi} [searchApi.$api] Search API.
 *
 * @description
 * Component for searching in media viewer.
 */
// eslint-disable-next-line max-classes-per-file
export default class KpMediaViewerSearchComponent {
    public static componentName = 'kpMediaViewerSearch';

    @ngComponentCssClass('media-viewer-search')
    public templateUrl = require('./kp-media-viewer-search.tpl.ftl');

    public bindings = {
        query: '<',
        focus: '<',
        onSearch: '&',
        onCancel: '&',
        onPrevious: '&',
        onNext: '&',
        searchApi: '&'
    };

    public controller = KpMediaViewerSearchComponentController;
}

interface KpMediaViewerSearchComponentBindings {
    query: string;
    focus: boolean;

    onSearch(locals: { $query: string }): Promise<number>;

    onPrevious(locals: { $index: number }): Promise<number>;

    onNext(locals: { $index: number }): Promise<number>;

    onCancel();

    searchApi(locals: { $api: SearchApi });
}
